import React, { useState, useEffect } from 'react';
import { 
    Card, 
    Tabs, 
    Typography, 
    Space, 
    Button, 
    Input, 
    Empty, 
    Spin,
    Row,
    Col,
    Statistic,
    Badge,
    Tag,
    Divider
} from 'antd';
import { 
    SearchOutlined, 
    ClockCircleOutlined, 
    CheckCircleOutlined, 
    CloseCircleOutlined,
    TruckOutlined,
    FireOutlined,
    StarOutlined
} from '@ant-design/icons';
import { useOrderTracking } from '../../contexts/OrderTrackingContext';
import OrderTrackingCard from '../OrderTracking/OrderTrackingCard';
import LoadingSkeleton from '../UI/LoadingSkeleton';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;
const { Search } = Input;

function OrderTracking() {
    const { 
        trackedOrders,
        orderHistory,
        loading,
        loadOrderHistory,
        getActiveOrders,
        getOrdersByStatus,
        isInitialized
    } = useOrderTracking();

    const [searchQuery, setSearchQuery] = useState('');
    const [activeTab, setActiveTab] = useState('active');
    const [filteredOrders, setFilteredOrders] = useState([]);

    // Load order history on mount
    useEffect(() => {
        if (isInitialized) {
            loadOrderHistory();
        }
    }, [isInitialized, loadOrderHistory]);

    // Filter orders based on search and tab
    useEffect(() => {
        let orders = [];
        
        if (activeTab === 'active') {
            orders = getActiveOrders();
        } else if (activeTab === 'delivered') {
            orders = getOrdersByStatus('delivered');
        } else if (activeTab === 'cancelled') {
            orders = getOrdersByStatus('cancelled');
        } else {
            orders = orderHistory;
        }

        // Apply search filter
        if (searchQuery) {
            orders = orders.filter(order => 
                order.order_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                order.vendor?.business_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                order.status?.toLowerCase().includes(searchQuery.toLowerCase())
            );
        }

        setFilteredOrders(orders);
    }, [activeTab, searchQuery, trackedOrders, orderHistory, getActiveOrders, getOrdersByStatus]);

    // Get order statistics
    const getOrderStats = () => {
        const active = getActiveOrders().length;
        const delivered = getOrdersByStatus('delivered').length;
        const cancelled = getOrdersByStatus('cancelled').length;
        const total = orderHistory.length;

        return { active, delivered, cancelled, total };
    };

    const stats = getOrderStats();

    // Render order statistics
    const renderOrderStats = () => (
        <Row gutter={16} className="mb-6">
            <Col span={6}>
                <Card>
                    <Statistic
                        title="Active Orders"
                        value={stats.active}
                        prefix={<ClockCircleOutlined className="text-blue-500" />}
                    />
                </Card>
            </Col>
            <Col span={6}>
                <Card>
                    <Statistic
                        title="Delivered"
                        value={stats.delivered}
                        prefix={<CheckCircleOutlined className="text-green-500" />}
                    />
                </Card>
            </Col>
            <Col span={6}>
                <Card>
                    <Statistic
                        title="Cancelled"
                        value={stats.cancelled}
                        prefix={<CloseCircleOutlined className="text-red-500" />}
                    />
                </Card>
            </Col>
            <Col span={6}>
                <Card>
                    <Statistic
                        title="Total Orders"
                        value={stats.total}
                        prefix={<StarOutlined className="text-purple-500" />}
                    />
                </Card>
            </Col>
        </Row>
    );

    // Render search and filters
    const renderSearchAndFilters = () => (
        <Card className="mb-6">
            <div className="flex items-center justify-between mb-4">
                <Title level={4} className="mb-0">Order Tracking</Title>
                <Button 
                    type="primary" 
                    onClick={() => loadOrderHistory()}
                    loading={loading}
                >
                    Refresh
                </Button>
            </div>
            
            <Search
                placeholder="Search by order number, vendor, or status..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onSearch={setSearchQuery}
                enterButton={<SearchOutlined />}
                className="mb-4"
            />

            <Tabs 
                activeKey={activeTab} 
                onChange={setActiveTab}
                items={[
                    {
                        key: 'active',
                        label: (
                            <span>
                                <ClockCircleOutlined />
                                Active Orders
                                {stats.active > 0 && (
                                    <Badge count={stats.active} size="small" className="ml-2" />
                                )}
                            </span>
                        )
                    },
                    {
                        key: 'delivered',
                        label: (
                            <span>
                                <CheckCircleOutlined />
                                Delivered
                                {stats.delivered > 0 && (
                                    <Badge count={stats.delivered} size="small" className="ml-2" />
                                )}
                            </span>
                        )
                    },
                    {
                        key: 'cancelled',
                        label: (
                            <span>
                                <CloseCircleOutlined />
                                Cancelled
                                {stats.cancelled > 0 && (
                                    <Badge count={stats.cancelled} size="small" className="ml-2" />
                                )}
                            </span>
                        )
                    },
                    {
                        key: 'all',
                        label: (
                            <span>
                                <StarOutlined />
                                All Orders
                                {stats.total > 0 && (
                                    <Badge count={stats.total} size="small" className="ml-2" />
                                )}
                            </span>
                        )
                    }
                ]}
            />
        </Card>
    );

    // Render orders list
    const renderOrdersList = () => {
        if (loading) {
            return (
                <div className="space-y-4">
                    {Array.from({ length: 3 }).map((_, index) => (
                        <LoadingSkeleton key={index} type="card" />
                    ))}
                </div>
            );
        }

        if (filteredOrders.length === 0) {
            return (
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                        <div className="text-center">
                            <Title level={4}>No orders found</Title>
                            <Paragraph type="secondary">
                                {searchQuery 
                                    ? 'No orders match your search criteria'
                                    : activeTab === 'active' 
                                        ? 'You have no active orders'
                                        : 'No orders in this category'
                                }
                            </Paragraph>
                        </div>
                    }
                />
            );
        }

        return (
            <div className="space-y-4">
                {filteredOrders.map((order) => (
                    <OrderTrackingCard
                        key={order.id}
                        orderId={order.id}
                        orderData={order}
                        showActions={activeTab === 'active'}
                        compact={false}
                    />
                ))}
            </div>
        );
    };

    // Render empty state
    const renderEmptyState = () => (
        <div className="text-center py-12">
            <ClockCircleOutlined style={{ fontSize: 64, color: '#d9d9d9' }} />
            <Title level={3} className="mt-4 text-gray-500">
                No Orders Yet
            </Title>
            <Paragraph type="secondary" className="text-lg">
                Your order history will appear here once you place an order
            </Paragraph>
            <Button type="primary" size="large" href="/menu">
                Browse Menu
            </Button>
        </div>
    );

    if (!isInitialized) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
                <div className="container mx-auto px-4">
                    <div className="text-center py-12">
                        <Spin size="large" />
                        <Title level={3} className="mt-4">Loading Order Tracking...</Title>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
            <div className="container mx-auto px-4">
                {/* Header */}
                <div className="text-center mb-8">
                    <Title level={1} className="mb-4">
                        Order Tracking
                    </Title>
                    <Paragraph type="secondary" className="text-lg">
                        Track your orders in real-time
                    </Paragraph>
                </div>

                {/* Order Statistics */}
                {stats.total > 0 && renderOrderStats()}

                {/* Search and Filters */}
                {stats.total > 0 && renderSearchAndFilters()}

                {/* Orders List */}
                {stats.total > 0 ? renderOrdersList() : renderEmptyState()}
            </div>
        </div>
    );
}

export default OrderTracking;
